/**
 * The client half of the AI chat's `editConfig` tool: it validates and normalises
 * the model's arguments into a transaction, then hands them to the Distro editor.
 *
 * Split out of FloatingAIChat so it can be unit tested — the schema, id, and
 * reference checks live in @aidkitorg/typesheets/lib/configEdit; this layer is
 * only responsible for turning tool arguments into a well-formed op batch and for
 * the chat-specific policies (write gating, translation-marker guard, result
 * size bounding).
 */
import { parseConfigPath, type ConfigEditOp, type ConfigTransactionResult } from '@aidkitorg/typesheets/lib/configEdit';
import { parseConfigEditOps } from '@aidkitorg/typesheets/lib/configAgent';
export { jsonParseFailureDetail, OMITTED_TRANSLATION_MARKER } from '@aidkitorg/typesheets/lib/configAgent';

/**
 * Applies one atomic transaction and returns the engine result. All edits land
 * together in one changeset, or nothing changes. `verbatimValues` marks the
 * single write op's value as machine-copied from an existing document (see
 * TransactionOptions in @aidkitorg/typesheets configEdit) — only runCopyConfig
 * sets it; model-authored edits must never.
 */
export type TransactionApplier = (ops: ConfigEditOp[], opts?: { dryRun?: boolean, verbatimValues?: boolean }) => ConfigTransactionResult;

export type EditToolInput = { edits?: string };

/**
 * `edits` arrives as ONE JSON string containing the ops array: models produce a
 * top-level JSON string reliably, and keeping the values as plain JSON inside it
 * avoids double-encoding.
 */
export type EditGates = {
  editModeEnabled: boolean;
  stopRequested?: boolean;
  branchChanged?: boolean;
  diverged?: boolean;
  /**
   * PII Mode. The server also omits the edit tools for these requests, but that alone
   * loses the race: these tools execute in the browser, so a turn already streaming when
   * the mode is switched on still delivers its tool call.
   */
  piiMode?: boolean;
};

export function runEditConfig(
  input: EditToolInput,
  applyTransaction: TransactionApplier | undefined,
  gates: EditGates,
): Record<string, unknown> {
  // The gate lives HERE, not at call sites, so no caller can reach the write
  // path without it.
  const refused = editGateMessage(gates);
  if (refused) {
    return { error: refused };
  }
  if (!applyTransaction) {
    return { error: 'Config editing is not available in this editor.' };
  }
  const parsed = parseConfigEditOps(input.edits);
  if ('error' in parsed) return parsed;

  // No dry-run mode: a rejected transaction already changes nothing, so a
  // rehearsal pass would only double the cost of every cautious edit.
  return applyTransaction(parsed.ops) as unknown as Record<string, unknown>;
}

/**
 * Walks a plain document by a getObject-style path and returns the RAW value —
 * no language stripping, no depth summarizing. Same parser editConfig writes
 * through, so any readable path is also a writable one.
 */
export function resolveRawPath(doc: unknown, path: string): { value: unknown } | { error: string } {
  const segments = parseConfigPath(path);
  if (!segments) {
    return { error: `Could not parse path "${path}". Use property and index access only, e.g. "survey[0].components[1]".` };
  }
  let current: any = doc;
  for (const segment of segments) {
    current = segment.kind === 'prop' ? current?.[segment.prop] : current?.[segment.index];
    if (current === undefined || current === null) {
      return { error: `Path not found: ${path}` };
    }
  }
  return { value: current };
}

export type CopyToolInput = {
  fromPath?: unknown;
  toPath?: unknown;
  op?: unknown;
  index?: unknown;
};

/**
 * The `copyConfigObject` tool: copy a raw subtree (every language, exact values —
 * nothing passes through the model) from a source document into the OPEN document
 * via the same gated transaction engine as editConfig. The caller resolves which
 * document the source path is read from; the write target is always the open one.
 */
export function runCopyConfig(
  input: CopyToolInput,
  resolveSource: (path: string) => { value: unknown } | { error: string },
  applyTransaction: TransactionApplier | undefined,
  gates: EditGates,
): Record<string, unknown> {
  // A copy IS a write — identical gating to editConfig, enforced here so no
  // call site can reach the write path without it.
  const refused = editGateMessage(gates);
  if (refused) {
    return { error: refused };
  }
  if (!applyTransaction) {
    return { error: 'Config editing is not available in this editor.' };
  }
  if (typeof input.fromPath !== 'string' || input.fromPath === '') {
    return { error: '"fromPath" is required: the path of the object to copy.' };
  }
  if (typeof input.toPath !== 'string' || input.toPath === '') {
    return { error: '"toPath" is required: where to put the copy in the open document.' };
  }
  const op = input.op === undefined ? 'insert' : input.op;
  if (op !== 'insert' && op !== 'replace') {
    return { error: `"op" must be "insert" (into the array at toPath) or "replace" (overwrite toPath), not "${String(input.op)}".` };
  }
  if (input.index !== undefined && (typeof input.index !== 'number' || !Number.isInteger(input.index) || input.index < 0)) {
    return { error: '"index" must be a whole number of 0 or more.' };
  }
  if (op === 'replace' && input.index !== undefined) {
    return { error: '"index" only applies to insert; for replace, put the index in toPath itself.' };
  }

  const source = resolveSource(input.fromPath);
  if ('error' in source) {
    return { error: source.error };
  }

  const ops: ConfigEditOp[] = [{
    op,
    path: input.toPath,
    value: source.value,
    ...(op === 'insert' && input.index !== undefined && { index: input.index }),
  } as ConfigEditOp];
  // verbatimValues: the value came straight from an existing document, so stray
  // legacy fields inside it are tolerated (as human paste tolerates them) —
  // model-authored editConfig writes stay strict.
  const result = applyTransaction(ops, { verbatimValues: true }) as unknown as Record<string, unknown>;
  return {
    ...result,
    copied: { fromPath: input.fromPath, toPath: input.toPath, op, size: JSON.stringify(source.value)?.length ?? 0 },
  };
}

/**
 * Whether a tool call is allowed to write right now, checked independently of the
 * server's decision to expose the tool at all — a call can arrive from a turn that
 * was already in flight.
 *
 * `stopRequested`: a write is not idempotent like the read tools; a buffered tool
 * call arriving after Stop would change the document while the transcript says
 * cancelled. `branchChanged`: authorization is per-branch and the reset effect
 * runs too late to stop a callback already streaming. `diverged`: a previous
 * transaction's replay did not match its validated candidate — the document is in
 * an unverified state and nothing more may be written until the user reloads.
 */
export function editGateMessage({
  editModeEnabled,
  stopRequested = false,
  branchChanged = false,
  diverged = false,
  piiMode = false,
}: EditGates): string | null {
  // First, because it is the reason the user will recognise: they just turned the mode on,
  // and no other refusal is actionable while it is.
  if (piiMode) {
    return 'PII Mode is on, so config editing is disabled — the conversation may contain '
      + 'applicant data and nothing here may be written into the config. Describe the change '
      + 'instead, and the user can apply it in a normal session.';
  }
  if (diverged) {
    return 'A previous edit left the document in an unverified state. Config editing is disabled — '
      + 'ask the user to reload the page (and consider discarding this branch) before any further edits.';
  }
  if (!editModeEnabled) {
    return 'Config editing is turned off. Ask the user to enable "Allow edits" in the chat panel.';
  }
  if (stopRequested) {
    return 'The user stopped this request before the edit ran, so nothing was changed.';
  }
  if (branchChanged) {
    return 'The branch changed since editing was authorized, so nothing was changed. '
      + 'Ask the user to re-enable "Allow edits" for the branch they are now on.';
  }
  return null;
}

/** Tool results above this go back to the model trimmed. */
const MAX_TOOL_RESULT_CHARS = 50_000;
/** Errors kept verbatim when a result has to be trimmed. */
const TRIMMED_ERROR_COUNT = 5;

/**
 * Bounds an oversized tool result.
 *
 * A WRITE result keeps its outcome AND a useful error prefix no matter what: the
 * engine already bounds diagnostics, but if a result is still too large, an agent
 * that can't see example errors can't repair the transaction — and one that
 * thinks a committed edit failed will retry an edit that already landed.
 *
 * A READ result is safe to replace outright, since nothing happened.
 */
export function boundToolResult(result: Record<string, unknown>): Record<string, unknown> {
  const json = JSON.stringify(result);
  if (json.length <= MAX_TOOL_RESULT_CHARS) return result;

  if ('ok' in result) {
    const errors = Array.isArray(result.errors) ? result.errors as string[] : [];
    const warnings = Array.isArray(result.warnings) ? result.warnings as string[] : [];
    const clip = (list: string[]) => list.slice(0, TRIMMED_ERROR_COUNT).map(e => typeof e === 'string' ? e.slice(0, 2_000) : e);
    const shownErrors = clip(errors);
    // Warnings matter on SUCCESS: the edit landed, and dangling-reference
    // warnings are the agent's only pointer at what it still owes the config.
    const shownWarnings = clip(warnings);
    return {
      ok: result.ok,
      ...(result.applied !== undefined && { applied: result.applied }),
      ...(result.committed !== undefined && { committed: result.committed }),
      ...(result.diverged !== undefined && { diverged: result.diverged }),
      ...(result.failedOpIndex !== undefined && { failedOpIndex: result.failedOpIndex }),
      ...(shownErrors.length > 0 && { errors: shownErrors }),
      ...(errors.length > 0 && { errorCount: errors.length + (typeof result.omittedErrorCount === 'number' ? result.omittedErrorCount : 0) }),
      ...(shownWarnings.length > 0 && { warnings: shownWarnings }),
      ...(warnings.length > 0 && { warningCount: warnings.length + (typeof result.omittedWarningCount === 'number' ? result.omittedWarningCount : 0) }),
      note: result.ok
        ? 'The result was too large; only the first warnings are shown. They remain true until addressed.'
        : 'The result was too large; only the first errors are shown. Fix these, then re-run to see the rest.',
    };
  }
  return {
    error: `Result too large (${json.length.toLocaleString()} chars). Please narrow your search — `
      + 'use a more specific pattern or add the "field" parameter (e.g. field: "kind") to restrict matching.',
    totalResults: result.totalCount,
    pattern: result.pattern,
  };
}
